import { API_ENDPOINTS, httpClientInstance } from "@/api"
import httpClient from "@/api/http-client"
import { create } from "zustand"

export interface AuditReport {
  id: number
  aws_account_id: string
  region: string
  report_scope: string
  total_resources: number
  compliant_resources: number
  non_compliant_resources: number
  status: string
  owner_email: string
  generated_at: string
  updated_at: string
}

export interface EvidenceSnapshot {
  id: number
  evidence_type: string
  raw_payload: Record<string, unknown>
  captured_at: string
}

export interface ResourceResult {
  id: number
  ec2_instance_id: string
  instance_arn: string
  name: string
  state: string
  instance_type: string
  ami_id: string
  vpc_id: string
  subnet_id: string
  availability_zone: string
  private_ip: string
  public_ip: string
  security_groups: string[]
  tags: Record<string, string>
  cloudspry_owner_email: string
  compliance_status: string
  compliance_findings: string[]
  checked_at: string
  updated_at: string
  evidence_snapshots: EvidenceSnapshot[]
}

export interface CreateAuditReportResponse {
  id: number
  aws_account_id: string
  region: string
  report_scope: string
  total_resources: number
  compliant_resources: number
  non_compliant_resources: number
  status: string
  metadata: {
    completed_at: string
    requested_by_email: string
    department_id?: number
    department_name?: string
  }
  generated_at: string
  updated_at: string
  owner: {
    id: number
    user: {
      id: number
      username: string
      email: string
      first_name: string
      last_name: string
    }
    email: string
    organization: string
    budget_threshold: string
    total_instances: number
    total_monthly_cost: number
    created_at: string
    updated_at: string
  }
  resource_results: ResourceResult[]
}

interface AuditReportsStore {
  auditReports: AuditReport[]
  isLoading: boolean
  isCreating: boolean
  isFetchingDetail: boolean
  exportingHtmlId: number | null
  exportingCsvId: number | null
  error: string | null
  lastCreated: CreateAuditReportResponse | null
  fetchAuditReports: () => Promise<void>
  fetchAuditReportById: (id: number) => Promise<CreateAuditReportResponse>
  createAuditReport: (region: string, departmentId?: number | null) => Promise<CreateAuditReportResponse>
  exportHtml: (id: number, filename?: string) => Promise<void>
  exportCsv: (id: number, filename?: string) => Promise<void>
  clearLastCreated: () => void
}

export const useAuditReportsStore = create<AuditReportsStore>((set, get) => ({
  auditReports: [],
  isLoading: false,
  isCreating: false,
  isFetchingDetail: false,
  exportingHtmlId: null,
  exportingCsvId: null,
  error: null,
  lastCreated: null,

  fetchAuditReports: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await httpClientInstance.get<AuditReport[]>(API_ENDPOINTS.awsConnect.auditReports.list)
      set({ auditReports: data, isLoading: false })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to fetch audit reports"
      set({ error: message, isLoading: false })
    }
  },

  fetchAuditReportById: async (id: number) => {
    set({ isFetchingDetail: true, error: null })
    try {
      const data = await httpClientInstance.get<CreateAuditReportResponse>(
        API_ENDPOINTS.awsConnect.auditReports.retrieve(id)
      )
      set({ isFetchingDetail: false })
      return data
    } catch (err: unknown) {
      const axiosErr = err as any
      const message =
        axiosErr?.response?.data?.error ||
        axiosErr?.response?.data?.detail ||
        (err instanceof Error ? err.message : "Failed to fetch audit report")
      set({ error: message, isFetchingDetail: false })
      throw new Error(message)
    }
  },

  createAuditReport: async (region: string, departmentId?: number | null) => {
    set({ isCreating: true, error: null })
    try {
      const data = await httpClientInstance.post<CreateAuditReportResponse>(
        API_ENDPOINTS.awsConnect.auditReports.create,
        { region, department_id: departmentId ?? null }
      )
      set({ lastCreated: data, isCreating: false })
      // Refresh the list so the new report appears
      await get().fetchAuditReports()
      return data
    } catch (err: unknown) {
      const axiosErr = err as any
      const message =
        axiosErr?.response?.data?.error ||
        axiosErr?.response?.data?.detail ||
        (err instanceof Error ? err.message : "Failed to create audit report")
      set({ error: message, isCreating: false })
      throw new Error(message)
    }
  },

  exportHtml: async (id: number, filename?: string) => {
    set({ exportingHtmlId: id })
    try {
      const response = await httpClient.get<string>(API_ENDPOINTS.awsConnect.auditReports.exportHtml(id), {
        responseType: "text",
      })
      const blob = new Blob([response.data], { type: "text/html" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename ?? `audit-report-${id}.html`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      set({ exportingHtmlId: null })
    }
  },

  exportCsv: async (id: number, filename?: string) => {
    set({ exportingCsvId: id })
    try {
      const response = await httpClient.get<string>(API_ENDPOINTS.awsConnect.auditReports.exportCsv(id), {
        responseType: "text",
      })
      const blob = new Blob([response.data], { type: "text/csv" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename ?? `audit-report-${id}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      set({ exportingCsvId: null })
    }
  },

  clearLastCreated: () => set({ lastCreated: null }),
}))
